import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle, AlertCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getPaymentStatus } from "../api";
import { CONTACTS } from "../mock";

const MAX_TRIES = 20;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  let sessionId = params.get("session_id");
  if (!sessionId && !cancelled && typeof window !== "undefined") {
    try { sessionId = localStorage.getItem("cm_reservation"); } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("localStorage non disponibile:", e); }
  }
  const [state, setState] = useState(cancelled ? "cancelled" : "polling"); // polling | paid | pending | error | cancelled
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const d = await getPaymentStatus(sessionId);
        setInfo(d);
        if (d.payment_status === "paid") {
          setState("paid");
          try { localStorage.removeItem("cm_reservation"); } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("localStorage non disponibile:", e); }
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) { setState("error"); return; }
        tries += 1;
        if (tries >= MAX_TRIES) { setState("pending"); return; }
        timer = setTimeout(poll, 3000);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("Verifica pagamento non riuscita:", e);
        tries += 1;
        if (tries >= MAX_TRIES) { setState("error"); return; }
        timer = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-poll only when session/cancel changes
  }, [sessionId, cancelled]);

  const Wrap = ({ children }) => (
    <>
      <Header />
      <main className="mx-auto flex min-h-[60vh] max-w-[760px] items-center px-6 pt-[120px] lg:px-10">
        <div className="w-full rounded-[18px] border border-[var(--cm-line)] bg-white p-10 text-center">
          {children}
        </div>
      </main>
      <Footer />
    </>
  );

  if (state === "polling")
    return (
      <Wrap>
        <Loader2 size={40} className="mx-auto animate-spin text-[#8a8a82]" />
        <h1 className="mt-5 text-[1.5rem] font-semibold">Verifica del pagamento…</h1>
        <p className="mt-2 text-[#6f6f68]">Attendi qualche secondo, stiamo confermando dal server.</p>
      </Wrap>
    );

  if (state === "paid")
    return (
      <Wrap>
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--cm-green)]">
          <Check size={32} className="text-white" strokeWidth={3} />
        </div>
        <h1 className="text-[1.7rem] font-semibold">Prenotazione confermata</h1>
        <p className="mx-auto mt-3 max-w-[440px] leading-relaxed text-[#6f6f68]">
          Importo di prenotazione (rimborsabile) ricevuto{info?.car_title ? ` per ${info.car_title}` : ""}
          {info?.car_ref ? ` · Rif. ${info.car_ref}` : ""}. Ti abbiamo inviato la ricevuta via email
          e ti contatteremo per fissare la visione e la prova su strada.
        </p>
        <Link to="/auto" className="mt-6 inline-block rounded-full bg-[var(--cm-ink)] px-6 py-3 text-[0.9rem] font-medium text-white">
          Torna alle auto
        </Link>
      </Wrap>
    );

  if (state === "pending")
    return (
      <Wrap>
        <AlertCircle size={40} className="mx-auto text-[#c48a12]" />
        <h1 className="mt-5 text-[1.5rem] font-semibold">Pagamento in elaborazione</h1>
        <p className="mx-auto mt-2 max-w-[440px] text-[#6f6f68]">
          Il pagamento è in fase di conferma. Se hai completato il checkout riceverai l'email a breve.
          Per assistenza chiama lo {CONTACTS.ufficio}.
        </p>
        <Link to="/auto" className="mt-6 inline-block rounded-full border border-[var(--cm-line)] px-6 py-3 text-[0.9rem] font-medium">
          Torna alle auto
        </Link>
      </Wrap>
    );

  if (state === "cancelled")
    return (
      <Wrap>
        <XCircle size={40} className="mx-auto text-[#8a8a82]" />
        <h1 className="mt-5 text-[1.5rem] font-semibold">Pagamento annullato</h1>
        <p className="mx-auto mt-2 max-w-[440px] text-[#6f6f68]">
          Nessun addebito effettuato. Puoi riprovare quando vuoi o contattarci allo {CONTACTS.ufficio}.
        </p>
        <Link to="/auto" className="mt-6 inline-block rounded-full bg-[var(--cm-ink)] px-6 py-3 text-[0.9rem] font-medium text-white">
          Torna alle auto
        </Link>
      </Wrap>
    );

  return (
    <Wrap>
      <XCircle size={40} className="mx-auto text-[#b42318]" />
      <h1 className="mt-5 text-[1.5rem] font-semibold">Pagamento non riuscito</h1>
      <p className="mx-auto mt-2 max-w-[440px] text-[#6f6f68]">
        Non siamo riusciti a confermare il pagamento. Nessuna prenotazione è stata registrata.
        Riprova o chiamaci allo {CONTACTS.ufficio}.
      </p>
      <Link to="/auto" className="mt-6 inline-block rounded-full border border-[var(--cm-line)] px-6 py-3 text-[0.9rem] font-medium">
        Torna alle auto
      </Link>
    </Wrap>
  );
}
